import React, { useMemo, useRef } from 'react';

import { cssMap } from '@compiled/react';

import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Stack } from '@atlaskit/primitives/compiled';

import { parseLuminaTagsToJSON } from './parser/tag-parser';
import { LuminaSection } from './section/lumina-section';
import type { LuminaSubAgentResponseType, ParsedLayout, ParsedSubAgentDataMap } from './types';
import { getFirstFoldableSectionIdx } from './utils';

interface LuminaLayoutProps {
	isStreaming?: boolean;
	luminaSubAgentResponse?: LuminaSubAgentResponseType[];
	luminaTagResponse: string;
}

const styles = cssMap({
	containerStyles: {
		textAlign: 'left',
	},
});

export const LuminaLayout = ({
	luminaTagResponse,
	luminaSubAgentResponse,
	isStreaming = false,
}: LuminaLayoutProps): React.JSX.Element => {
	// Refs for parser stability optimization
	const parsedAnswerRef = useRef<ParsedLayout>([]);
	const sectionHashCacheRef = useRef<Map<number, string>>(new Map());

	const parsedAnswer = parseLuminaTagsToJSON(
		luminaTagResponse,
		parsedAnswerRef.current,
		sectionHashCacheRef.current,
	);

	// Update the ref after parsing for next render
	parsedAnswerRef.current = parsedAnswer;

	const firstFoldableIndex = getFirstFoldableSectionIdx(parsedAnswer);

	// Parse sub-agent responses once and create a lookup map
	const subAgentDataMap = useMemo((): ParsedSubAgentDataMap => {
		if (!luminaSubAgentResponse) {
			return {};
		}

		const map: ParsedSubAgentDataMap = {};

		luminaSubAgentResponse.forEach((response) => {
			try {
				if (response.sub_agent_name === 'PROJECT_HIGHLIGHTS') {
					map.projectHighlights = JSON.parse(response.data);
				} else if (response.sub_agent_name === 'ENTITY_RELATED_PEOPLE') {
					map.relatedPeople = JSON.parse(response.data);
				}
				// eslint-disable-next-line no-unused-vars
			} catch (_error) {
				// TODO: add proper error logging
			}
		});

		return map;
	}, [luminaSubAgentResponse]);

	return (
		<SmartCardProvider>
			<Box xcss={styles.containerStyles} testId="lumina-layout-container">
				<Stack>
					{parsedAnswer &&
						parsedAnswer.length &&
						parsedAnswer.map((section, index) => {
							const isFoldable = section.attributes?.folded === '1';
							const defaultFoldedState = isFoldable
								? index === firstFoldableIndex
									? 'expanded'
									: 'collapsed'
								: undefined;

							return (
								<LuminaSection
									key={index}
									section={section}
									subAgentDataMap={subAgentDataMap}
									defaultFoldedState={defaultFoldedState}
									isStreaming={isStreaming}
								/>
							);
						})}
				</Stack>
			</Box>
		</SmartCardProvider>
	);
};
